import React, { useState, useEffect } from 'react';
import {
  Box,
  Badge,
  Button,
  Heading,
  VStack,
  useToast,
  Flex,
  Tr,
  Table,
  Th,
  Td,
  Thead,
  Tbody,
  useColorMode,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Select
} from '@chakra-ui/react';
import { MdEdit, MdDelete, MdRefresh, MdAdd, MdSearch, MdClear, MdLockReset } from 'react-icons/md';
import { ChevronDownIcon } from '@chakra-ui/icons';
import Navigation from '../components/Navigation';
import axios from '../utils/axiosConfig';

const AdminAllUsersPage = () => {
  const { colorMode } = useColorMode();
  const [editFormData, setEditFormData] = useState({ 
    name: '', 
    email: '', 
    collectionName: '',
    role: 'user' 
  });
  const [selectedUserId, setSelectedUserId] = useState(null);
  const toast = useToast();
  const [isAdmin, setIsAdmin] = useState(null);
  const [users, setUsers] = useState([]);
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    const checkAuthentication = async () => {
      try {
        const response = await axios.get('/api/protected/checkIsAdmin');
        setIsAdmin(response.data.isAdmin);

      } catch (error) {
        console.error('Error checking session:', error);
        setIsAdmin(false);
      }
    };

    checkAuthentication();
    listUsers();
  }, []);

  const formatDateTime = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const listUsers = async () => {
    try {
      const response = await axios.get('/api/admin/users');
      if (response.data?.success && response.data?.data?.users) {
        setUsers(response.data.data.users);
      } else {
        toast({ 
          title: 'Failed to retrieve users', 
          status: 'error',
          description: 'Invalid response format'
        });
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({ 
        title: 'Failed to retrieve users', 
        status: 'error',
        description: error.response?.data?.message || 'An error occurred'
      });
    }
  };

  const handleEditUser = async (user) => {
    try {
      const response = await axios.get(`/api/admin/users/${user._id}/settings`);
      const settings = response.data.data.settings;
      
      setSelectedUserId(user._id);
      setEditFormData({
        name: user.name,
        email: user.email,
        collectionName: settings?.qdrantCollectionName || '',
        role: user.role || 'user'
      });
      onOpen();
    } catch (error) {
      toast({ title: 'Failed to fetch user settings', status: 'error' });
    }
  };

  const handleEditSubmit = async () => {
    try {
      await axios.put(`/api/admin/users/${selectedUserId}`, editFormData);
      toast({ title: 'User updated successfully!', status: 'success' });
      onClose();
      listUsers();
    } catch (error) {
      toast({ title: 'Failed to update user', status: 'error' });
    }
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const deleteUser = async(userId) => {
   
    try {
      const response = await axios.delete(`/api/admin/delete-user/${userId}`);
      if(response.data?.success) {
        await listUsers();
        toast({ title: 'User deleted successfully!', status: 'success' });
      } else {
        toast({ title: 'Failed to delete user', status: 'error' });
      }
    } catch (error) {
      toast({ title: 'Failed to delete user', status: 'error' });
    }
  };

  const resetUserPassword = async(userId) => {
    try {
      const response = await axios.post('/api/admin/reset-password', {userId});
      if(response.data?.success) {
        toast({ title: 'Password reset successfully!', status: 'success' });
      } else {
        toast({ title: response.data.message || 'Failed to reset user password', status: 'error' });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to reset user password';
      toast({ title: errorMessage, status: 'error' });
    }
  };

  const toggleUserSuspension = async (user) => {
    try {
      const endpoint = user.suspended ? 'unsuspend' : 'suspend';
      const response = await axios.put(`/api/admin/users/${user._id}/${endpoint}`);

      if (response.data?.success) {
        toast({
          title: response.data.message || `User ${endpoint}ed successfully`,
          status: 'success'
        });
        await listUsers();
      } else {
        toast({ title: `Failed to ${endpoint} user`, status: 'error' });
      }
    } catch (error) {
      toast({
        title: error.response?.data?.message || `Failed to ${user.suspended ? 'unsuspend' : 'suspend'} user`,
        status: 'error'
      });
    }
  };

  return !isAdmin ? (<></>) : (
    <Flex>
      <Navigation />
      <Box p={8} flex="1">
        <Heading size="lg" mb={6} color={colorMode === 'light' ? 'gray.800' : 'white'}>All Users</Heading>
        <VStack spacing={4} align="stretch">
          <Table variant="simple" size="lg">
            <Thead position="sticky" top={0} bg={colorMode === 'light' ? 'gray.100' : 'gray.700'} zIndex={1}>
              <Tr>
                <Th color={colorMode === 'light' ? 'gray.700' : 'gray.200'}>Name</Th>
                <Th color={colorMode === 'light' ? 'gray.700' : 'gray.200'}>Email</Th>
                <Th color={colorMode === 'light' ? 'gray.700' : 'gray.200'}>Role</Th>
                <Th color={colorMode === 'light' ? 'gray.700' : 'gray.200'}>Status</Th>
                <Th color={colorMode === 'light' ? 'gray.700' : 'gray.200'}>Created On</Th>
                <Th color={colorMode === 'light' ? 'gray.700' : 'gray.200'}>Last Updated On</Th>
                <Th color={colorMode === 'light' ? 'gray.700' : 'gray.200'}>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {users.map((u) => (
                <Tr key={u._id}>
                  <Td color={colorMode === 'light' ? 'gray.700' : 'gray.200'}>{u.name}</Td>
                  <Td color={colorMode === 'light' ? 'gray.700' : 'gray.200'}>{u.email}</Td>
                  <Td color={colorMode === 'light' ? 'gray.700' : 'gray.200'}>{u.role}</Td>
                  <Td>
                    <Badge colorScheme={u.suspended ? 'red' : 'green'}>
                      {u.suspended ? 'Suspended' : 'Active'}
                    </Badge>
                  </Td>
                  <Td color={colorMode === 'light' ? 'gray.700' : 'gray.200'}>{formatDateTime(u.createdAt)}</Td>
                  <Td color={colorMode === 'light' ? 'gray.700' : 'gray.200'}>{formatDateTime(u.updatedAt)}</Td>
                  <Td>
                    <Button colorScheme="blue" onClick={() => handleEditUser(u)} mr={2}>Edit</Button>
                    <Button
                      colorScheme={u.suspended ? 'green' : 'yellow'}
                      onClick={() => toggleUserSuspension(u)}
                      mr={2}
                      isDisabled={u.role === 'admin'}
                    >
                      {u.suspended ? 'Unsuspend' : 'Suspend'}
                    </Button>
                    <Button 
                      colorScheme="red" 
                      onClick={() => deleteUser(u._id)} 
                      mr={2}
                    >
                      Delete
                    </Button>
                    <Button colorScheme="orange" onClick={() => resetUserPassword(u._id)}>Reset Password</Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </VStack>

        {/* Edit User Modal */}
        <Modal isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Edit User</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4}>
                <FormControl>
                  <FormLabel>Name</FormLabel>
                  <Input
                    name="name"
                    value={editFormData.name}
                    onChange={handleEditInputChange}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Email</FormLabel>
                  <Input
                    name="email"
                    type="email"
                    value={editFormData.email}
                    onChange={handleEditInputChange}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Role</FormLabel>
                  <Select
                    name="role"
                    value={editFormData.role}
                    onChange={handleEditInputChange}
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>Collection Name</FormLabel>
                  <Input
                    name="collectionName"
                    value={editFormData.collectionName}
                    onChange={handleEditInputChange}
                  />
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="blue" mr={3} onClick={handleEditSubmit}>
                Save Changes
              </Button>
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    </Flex>
  );
};

export default AdminAllUsersPage; 
